import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { IDLE_TODAY_EMPTY_HOST, type TodayEmptyHost } from './useTodayAnyway';

// The host's side of the empty «today» card: its coverage loads only while
// such a draft is open and empty, not every time the Workouts screen mounts.
// `useHost` is the platform's hook (a module-level function, so its identity
// never changes). #492 F5: it reads the coverage on the draft's scope.

type HostScope = { bodyweightOnly: boolean };

// Mounted only while the draft is empty. Renders nothing; reports up.
function TodayEmptyProbe({
  useHost,
  scope,
  onHost,
}: {
  useHost: (scope: HostScope) => TodayEmptyHost;
  scope: HostScope;
  onHost: (host: TodayEmptyHost) => void;
}) {
  const { onBuildAnyway, weeklyMinimumMet } = useHost(scope);
  useEffect(() => {
    onHost({ onBuildAnyway, weeklyMinimumMet });
  }, [onBuildAnyway, weeklyMinimumMet, onHost]);
  useEffect(() => () => onHost(IDLE_TODAY_EMPTY_HOST), [onHost]);
  return null;
}

/** `probe` goes in the tree before the LoggerScreen; `host` and `onEmptyChange`
 *  go to its props. */
export function useTodayEmptyState(useHost: (scope: HostScope) => TodayEmptyHost): {
  probe: ReactNode;
  host: TodayEmptyHost;
  onEmptyChange: (empty: boolean, bodyweightOnly?: boolean) => void;
} {
  const [open, setOpen] = useState(false);
  const [bodyweightOnly, setBodyweightOnly] = useState(false);
  const [host, setHost] = useState<TodayEmptyHost>(IDLE_TODAY_EMPTY_HOST);
  const onEmptyChange = useCallback((empty: boolean, bw?: boolean) => {
    setOpen(empty);
    if (empty) setBodyweightOnly(bw === true);
  }, []);
  const scope = useMemo(() => ({ bodyweightOnly }), [bodyweightOnly]);
  return {
    probe: open ? <TodayEmptyProbe useHost={useHost} scope={scope} onHost={setHost} /> : null,
    host,
    onEmptyChange,
  };
}
